import { closeAnchoredMenu, openAnchoredMenu } from '../components/ui/AnchoredMenu'
import { getCsrfToken } from './csrf'
import { readJsonData } from './serverData'

/**
 * The menu behind the header avatar, and the answer to the one thing the
 * original handoff had nowhere for: signing out.
 *
 * Round two's `1a`. The avatar no longer navigates to Settings — Settings is
 * the menu's first item — because a control you need on a borrowed machine
 * cannot be three clicks deep, and because the menu is the only place that can
 * say *which account* it is about.
 *
 * Wired from the ERB header rather than a React island: the header is
 * server-rendered and Turbo-permanent, and `openAnchoredMenu` is a plain
 * function that renders into the overlay root. The avatar stays a real link to
 * Settings, so ⌘-click and no-JS both still work — the same rule every row in
 * the app follows.
 */

export interface AccountMenuData {
  name: string
  initials: string
  /** The address every item in this menu is about. */
  email: string
  /** Accounts this person has been given access to, if any. */
  accounts: { id: number; name: string }[]
  /** Set when switched into somebody else's account — the borrowed one. */
  viewing: { name: string; initials: string } | null
  /** Where "back to your account" posts, and where signing out posts. */
  paths: { settings: string; switchBack: string; signOut: string }
}

/** A form post, because both of these are state changes rather than reads. */
function post(action: string, method: 'delete' | 'post') {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = action
  form.style.display = 'none'

  const verb = document.createElement('input')
  verb.name = '_method'
  verb.value = method
  form.appendChild(verb)

  const token = document.createElement('input')
  token.name = 'authenticity_token'
  token.value = getCsrfToken()
  form.appendChild(token)

  document.body.appendChild(form)
  form.submit()
}

/** The repo's own 17px outline set, at the 13px a menu row takes. */
const Glyph = {
  settings: (
    <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.25">
      <circle cx="10" cy="10" r="2.6" />
      <path
        d="M10 2.6v2M10 15.4v2M2.6 10h2M15.4 10h2M4.8 4.8l1.4 1.4M13.8 13.8l1.4 1.4M15.2 4.8l-1.4 1.4M6.2 13.8l-1.4 1.4"
        strokeLinecap="round"
      />
    </svg>
  ),
  switch: (
    <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.25">
      <path d="M3 7h11l-3-3M17 13H6l3 3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  out: (
    <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.25">
      <path
        d="M12 6V4.5A1.5 1.5 0 0 0 10.5 3h-5A1.5 1.5 0 0 0 4 4.5v11A1.5 1.5 0 0 0 5.5 17h5a1.5 1.5 0 0 0 1.5-1.5V14"
        strokeLinecap="round"
      />
      <path d="M8.5 10h8.5l-2.5-2.5M17 10l-2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
}

let data: AccountMenuData | null = null

export function initAccountMenu() {
  const trigger = document.querySelector<HTMLElement>('[data-account-menu]')
  if (!trigger) return

  data = readJsonData<AccountMenuData>(trigger, 'data-account-menu')
  if (!data) return

  trigger.addEventListener('click', (event) => {
    // A real link underneath, so ⌘-click, middle-click and no-JS all reach
    // Settings — the menu is the ordinary path, not the only one.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    open(trigger)
  })
}

function open(trigger: HTMLElement) {
  if (!data) return

  openAnchoredMenu(trigger, data.viewing ? borrowedSpec() : ownSpec())
}

/** Your own account: Settings, the accounts you can open, and the way out. */
function ownSpec() {
  const account = data!
  const items = [
    { value: 'settings', label: 'Settings', note: '⌘,', glyph: Glyph.settings },
    // Hidden rather than disabled when there is nothing to switch to: an item
    // that cannot do anything is a question the reader has to answer.
    ...(account.accounts.length > 0
      ? [{ value: 'switch', label: 'Switch account', note: `${account.accounts.length} ›`, glyph: Glyph.switch }]
      : []),
    {
      value: 'signOut',
      label: 'Sign out',
      // The one two-line item in the product, and the second line is the
      // requirement: it names the account being ended.
      detail: account.email,
      glyph: Glyph.out,
      ruleBefore: true,
    },
  ]

  return {
    header: { initials: account.initials, name: account.name, detail: account.email },
    items,
    onPick: (value: string) => {
      if (value === 'settings') window.location.assign(account.paths.settings)
      if (value === 'switch') openAccounts()
      if (value === 'signOut') post(account.paths.signOut, 'delete')
    },
  }
}

/** The second level of "Switch account" — the `›` on that row means this. */
function openAccounts() {
  const account = data!
  const trigger = document.querySelector<HTMLElement>('[data-account-menu]')
  if (!trigger) return

  openAnchoredMenu(trigger, {
    title: 'Switch account',
    items: account.accounts.map((one) => ({ value: String(one.id), label: one.name })),
    onPick: (id) => post(`/settings/members/${id}/switch`, 'post'),
  })
}

/**
 * Switched into somebody else's account, where "sign out" has two possible
 * meanings and they are separated rather than disambiguated.
 */
function borrowedSpec() {
  const account = data!
  const viewing = account.viewing!

  return {
    header: {
      initials: viewing.initials,
      name: viewing.name,
      detail: account.email,
      borrowed: true,
      chip: 'VIEWING',
    },
    // The current item, at the top: leaving the borrowed account is the thing
    // somebody in this state is most likely here for.
    current: 'back',
    items: [
      { value: 'back', label: 'Back to your account', detail: account.email, glyph: Glyph.switch },
      { value: 'settings', label: `${viewing.name.split(' ')[0]}’s settings`, glyph: Glyph.settings },
      {
        value: 'signOut',
        label: 'Sign out of Wingspace',
        detail: 'Ends both sessions',
        glyph: Glyph.out,
        ruleBefore: true,
      },
    ],
    onPick: (value: string) => {
      if (value === 'back') post(account.paths.switchBack, 'delete')
      if (value === 'settings') window.location.assign(account.paths.settings)
      if (value === 'signOut') post(account.paths.signOut, 'delete')
    },
  }
}

/** For the ⌘K palette, which is what makes this reachable without a pointer. */
export function accountMenuData() {
  return data
}

/** The same act the menu's last item performs, for the palette to call. */
export function signOut() {
  if (data) post(data.paths.signOut, 'delete')
}

export { closeAnchoredMenu }
